<template>
    <div>
        <div v-for="(l, i) in lines" :key="i" class="caption" :class="l.cls">
            <v-icon x-small :color="l.color" class="mr-1">{{ l.icon }}</v-icon>{{ l.text }}
        </div>
        <div v-if="day.can_forgo || day.can_unforgo || day.move_dates.length || day.can_cancel_move"
            class="d-flex flex-wrap align-center mt-1" style="gap: 6px;">
            <v-btn v-if="day.can_forgo" x-small outlined color="primary" @click="openOffice">
                <v-icon x-small left>mdi-office-building-outline</v-icon>Coming to the office
            </v-btn>
            <v-btn v-if="day.can_unforgo" x-small text color="primary" :loading="busy === 'unforgo'" @click="unforgo">
                Take it back
            </v-btn>
            <v-btn v-if="day.move_dates.length" x-small outlined @click="openMove">
                <v-icon x-small left>mdi-calendar-arrow-right</v-icon>Change day
            </v-btn>
            <v-btn v-if="day.can_cancel_move" x-small text color="error" :loading="busy === 'cancel'" @click="cancelMove">
                Cancel request
            </v-btn>
        </div>
        <div v-if="detailed && hint" class="caption grey--text mt-1">{{ hint }}</div>

        <!-- Coming to the office instead -->
        <v-dialog v-model="office.open" max-width="420px">
            <v-card>
                <v-card-title class="subtitle-2">Coming to the office on {{ fmtDay(date) }}</v-card-title>
                <v-card-text>
                    <div class="caption mb-2">
                        HR and your section head are told you'll work from the office that day. WFH for that week is
                        given up — it can't be moved to another day or carried over. You can take it back until
                        {{ untilTime }} on {{ fmtDay(date) }}.
                    </div>
                    <v-textarea v-model="office.note" label="Note (optional)" outlined dense auto-grow rows="2" counter="500"></v-textarea>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn small text @click="office.open = false">Cancel</v-btn>
                    <v-btn small color="primary" :loading="busy === 'forgo'" @click="forgo">Tell HR</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Move to another day of the week -->
        <v-dialog v-model="move.open" max-width="440px">
            <v-card>
                <v-card-title class="subtitle-2">Change your WFH day ({{ fmtDay(date) }})</v-card-title>
                <v-card-text>
                    <div class="caption mb-2">
                        Choose another day of the same week. Your section head has to approve it by the day before the
                        new day — HR is told once it's approved. A week's WFH doesn't carry over to another week.
                    </div>
                    <div class="d-flex flex-wrap mb-3" style="gap: 6px;">
                        <v-btn v-for="d in day.move_dates" :key="d" small depressed
                            :color="move.date === d ? 'primary' : undefined" :outlined="move.date !== d" @click="move.date = d">
                            {{ fmtDay(d) }}
                        </v-btn>
                    </div>
                    <v-textarea v-model="move.reason" label="Reason (required)" outlined dense auto-grow rows="2" counter="1000"></v-textarea>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn small text @click="move.open = false">Cancel</v-btn>
                    <v-btn small color="primary" :disabled="!move.date || !move.reason.trim()" :loading="busy === 'move'" @click="requestMove">
                        Send to section head
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
// One approved WFH day: what became of it (moved, a move waiting, coming to the
// office, not used) and — on the staff member's own — asking to move it within
// its week or telling HR they're coming to the office. Every rule and window is
// the backend's (MonthlyScheduleService::wfhDay gives `day`); this shows it and
// sends the clicks. Used by the dashboard card (`detailed`: with the hint) and
// My schedule. Emits `changed` after a change.
export default {
    name: 'WfhDayActions',
    props: {
        entryId: { type: Number, required: true },
        date: { type: String, required: true },
        day: { type: Object, required: true },
        detailed: { type: Boolean, default: false },
    },
    data: () => ({
        busy: null,
        office: { open: false, note: '' },
        move: { open: false, date: null, reason: '' },
    }),
    computed: {
        base() {
            return `/api/monthly-schedule/my/entries/${this.entryId}`
        },
        untilTime() {
            return this.day.office_notice_until.slice(11, 16)
        },
        lines() {
            const d = this.day
            const out = []
            if (d.moved_from) {
                out.push({ icon: 'mdi-calendar-arrow-right', color: 'green darken-2', text: `Moved from ${this.fmtDay(d.moved_from)} · approved by ${d.move_approved_by}` })
            }
            if (d.move && d.move.open) {
                out.push({ icon: 'mdi-timer-sand', color: 'blue', cls: 'blue--text text--darken-2',
                    text: `Change to ${this.fmtDay(d.move.to)} waiting for the section head — needs approval by ${this.fmtDay(d.move.approve_by)}` })
            } else if (d.move) {
                out.push({ icon: 'mdi-alert-circle-outline', color: 'orange', cls: 'orange--text text--darken-2',
                    text: `Change to ${this.fmtDay(d.move.to)} wasn't approved in time` })
            }
            if (d.forgone_at) {
                out.push({ icon: 'mdi-office-building-outline', color: 'grey darken-1',
                    text: `Coming to the office — HR told ${this.fmtStamp(d.forgone_at)}. WFH for that week is given up.${d.forgone_note ? ' ' + d.forgone_note : ''}` })
            }
            if (d.state === 'unused') out.push({ icon: 'mdi-minus-circle-outline', color: 'grey', cls: 'grey--text', text: 'Not used' })
            if (d.state === 'lapsed') out.push({ icon: 'mdi-minus-circle-outline', color: 'grey', cls: 'grey--text', text: "Not used — that week's WFH has lapsed" })
            return out
        },
        hint() {
            const d = this.day
            if (d.state !== 'planned' || d.forgone_at) return ''
            if (d.can_forgo) return `If you'll come to the office instead, tell HR by ${this.untilTime} on ${this.fmtDay(this.date)}.`
            const now = new Date()
            if (`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}` < d.office_notice_from) {
                return `From ${this.fmtDay(d.office_notice_from)} until ${this.untilTime} on the day you can tell HR you're coming to the office instead.`
            }
            return ''
        },
    },
    methods: {
        openOffice() {
            this.office = { open: true, note: '' }
        },
        openMove() {
            this.move = { open: true, date: this.day.move_dates.length === 1 ? this.day.move_dates[0] : null, reason: '' }
        },
        forgo() {
            this.run('forgo', this.$axios.post(`${this.base}/office`, { note: this.office.note.trim() || null }), () => { this.office.open = false })
        },
        unforgo() {
            this.run('unforgo', this.$axios.delete(`${this.base}/office`))
        },
        requestMove() {
            this.run('move', this.$axios.post(`${this.base}/move`, { date: this.move.date, reason: this.move.reason.trim() }), () => { this.move.open = false })
        },
        cancelMove() {
            this.run('cancel', this.$axios.delete(`${this.base}/move`))
        },
        run(key, req, done) {
            this.busy = key
            req.then(() => {
                if (done) done()
                this.$emit('changed')
            }).finally(() => this.busy = null)
        },
        fmtDay(s) {
            const [, m, d] = s.split('-')
            const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date(`${s.slice(0, 10)}T00:00:00`).getDay()]
            return `${day} ${d.slice(0, 2)}/${m}`
        },
        fmtStamp(s) {
            return `${this.fmtDay(s.slice(0, 10))} ${s.slice(11, 16)}`
        },
    },
}
</script>
